import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { SaleOrderData } from '../models/sale-order-data-model';
import { ValidateSOModel } from '../models/validate-so-model';
import { map } from 'rxjs/operators';
import { OrderCancelModel } from '../models/order-cancel-model';
import { ValidateShippingDateInformation } from '../models/validate-shipping-date-information';
import { SaleOrderSearchModel } from '../../inquiries/models/sale-order-search-model';
import { SendASNResponseDTO } from 'app/core/operations/processing/models/ship.order';

@Injectable({
	providedIn: 'root'
})
export class SaleOrderService {

	/**
	 * URL to generate sale order WS
	 */
	API_GENERATE_SALE_ORDER_URL = environment.ipserver + 'o2c-backend/private/saleOrder/generate';

	/**
	 * URL to findID by sale order WS
	 */
	API_FIND_ID_URL = environment.ipserver + 'o2c-backend/private/saleOrder/findId';

	/**
	 * URL for sale order Information WS
	 */
	API_SALE_ORDER_INFORMATION_URL = environment.ipserver + 'o2c-backend/private/saleOrder/saleOrderInformation';
	API_SALE_EMP_ORDER_INFORMATION_URL = environment.ipserver + 'o2c-backend/private/saleOrder/saleEMPOrderInformation';

	/**
	 * URL to validate sale orders WS
	 */
	API_VALIDATE_SALE_ORDER_PROCESSED_URL = environment.ipserver + 'o2c-backend/private/saleOrder/validate';

	/**
	 * URL to update PO reference WS
	 */
	API_UPDATE_PO_REFERENCE_URL = environment.ipserver + 'o2c-backend/private/saleOrder/updatePOReference';

	/**
	 * URL to update notes
	 */
	API_UPDATE_NOTES_URL = environment.ipserver + 'o2c-backend/private/saleOrder/updateNotes';

	/**
	 * URL to get claim pdf
	 */
	API_CLAIM_PDF_URL = environment.ipserver + 'o2c-backend/private/saleOrder/generateClaimPDF';

	/**
	 * URL to validate shipping date
	 */
	API_VALIDATE_SHIPPING_DATE_URL = environment.ipserver + 'o2c-backend/private/saleOrder/validateShippingDate';

	/**
	 * URL to register the order cancellation
	 */
	API_REGISTER_CANCEL_ORDER_URL = environment.ipserver + 'o2c-backend/private/saleOrder/registerCancelledOrder';
	/**
	 * URL to sale order search
	 */
	API_SALE_ORDER_SEARCH_URL = environment.ipserver + 'o2c-backend/private/saleOrder/search';
	API_ASN_URL = environment.ipserver + 'o2c-backend/private/asn/sendEDIASN';
	API_WEEKLY_NON_EDI_REPORT_SCHEDULER_URL = environment.ipserver + 'o2c-backend/private/saleOrder/weeklyNonEdiReportScheduler';
	constructor(
		private httpC: HttpClient
	) { }

	/**
	 * Obtain a base64 hash with the pdf for Sale order
	 * @param gemDBKey db identifier
	 * @author Castor Transformación Digital - Alvaro Felipe Garcia Mendez
	 * @since 2020-07-17
	 */
	getSaleOrder(gemDBKey: string): Observable<any> {
		const paramsForReq = new HttpParams()
			.set('gemDBKey', gemDBKey);
		const type = 'text' as 'json';
		return this.httpC.get<any>(this.API_GENERATE_SALE_ORDER_URL, { params: paramsForReq, responseType: type });
	}

	/**
	 * Obtain a gemDBKey to get the sale order pdf
	 * @param orderNumber customer number
	 * @param division PO number
	 * @author Castor Transformación Digital - Alvaro Felipe Garcia Mendez
	 * @since 2020-07-17
	 */
	getgemDBKey(orderNumber: string, division: string): Observable<{ gemDBKey: number, orderNumber: string }> {
		const paramsForReq = new HttpParams()
			.set('orderNumber', orderNumber)
			.set('division', division);
		return this.httpC.get<{ gemDBKey: number, orderNumber: string }>(this.API_FIND_ID_URL, { params: paramsForReq });
	}

	/**
	 * Obtain all the info in a sales order for one gemDBKey
	 * @param gemDBKey db identifier
	 * @author Castor Transformación Digital - Alvaro Felipe Garcia Mendez
	 * @since 2020-09-04
	 */
	getSaleOrderInformation(gemDBKey: string): Observable<SaleOrderData> {
		const paramsForReq = new HttpParams()
			.set('gemDBKey', gemDBKey);
		return this.httpC.get<SaleOrderData>(this.API_SALE_ORDER_INFORMATION_URL, { params: paramsForReq });
	}
	getSaleEMPOrderInformation(gemDBKey: string): Observable<SaleOrderData> {
		const paramsForReq = new HttpParams()
			.set('gemDBKey', gemDBKey);
		return this.httpC.get<SaleOrderData>(this.API_SALE_EMP_ORDER_INFORMATION_URL, { params: paramsForReq });
	}

	/**
	 * Get the status of processing for a Sale order
	 * @param division division number
	 * @param orderNumber order number
	 * @author Castor Transformación Digital - Alvaro Felipe Garcia Mendez
	 * @since 2020-11-20
	 */
	getSOValidation(division: string, orderNumber: string): Observable<ValidateSOModel> {
		const paramsForReq = new HttpParams()
			.set('division', division)
			.set('orderNumber', orderNumber);
		return this.httpC.get<ValidateSOModel>(this.API_VALIDATE_SALE_ORDER_PROCESSED_URL, { params: paramsForReq });
	}

	/**
	 * Update the PO reference
	 * @param poNumber new po number to associate
	 * @param saleOrderId sale order id used for pdf
	 * @param masterId new master id to associate
	 * @author Castor Transformación Digital - Alvaro Felipe Garcia Mendez
	 * @since 2020-11-20
	 */
	putPOReference(poID: string, saleOrderId: number): Observable<boolean> {
		const paramsForReq = new HttpParams()
			.set('poId', poID)
			.set('saleOrderId', String(saleOrderId));
		const type = 'text' as 'json';
		return this.httpC.put<boolean>(this.API_UPDATE_PO_REFERENCE_URL, '', { params: paramsForReq, responseType: type })
			.pipe(
				map((auth) => {
					if (typeof auth === 'string') {
						auth = JSON.parse(auth);
					}
					return auth;
				})
			);
	}

	/**
	 * Update the PO reference
	 * @param saleOrderId new po number to associate
	 * @param notes sale order id used for pdf
	 * @author Castor Transformación Digital - Fabio Oquendo
	 * @since 2020-01-13
	 */
	updateNotes(saleOrderId: string, notes: string): Observable<boolean> {
		const paramsForReq = new HttpParams()
			.set('saleOrderId', saleOrderId)
			.set('notes', notes);
		const type = 'text' as 'json';
		return this.httpC.put<boolean>(this.API_UPDATE_NOTES_URL, '', { params: paramsForReq, responseType: type })
			.pipe(
				map((auth) => {
					if (typeof auth === 'string') {
						auth = JSON.parse(auth);
					}
					return auth;
				})
			);
	}

	/**
	 * Get claim pdf
	 * @param division division
	 * @param turClogNumber number for the claim
	 * @author Castor Transformación Digital - Alvaro Felipe Garcia Mendez
	 * @since 2021-01-20
	 */
	getClaimPDF(division: string, turClogNumber: string): any {
		const paramsForReq = new HttpParams()
			.set('turClogNumber', turClogNumber)
			.set('division', division);
		const type = 'text' as 'json';
		return this.httpC.get<any>(this.API_CLAIM_PDF_URL, { params: paramsForReq, responseType: type });
	}

	/**
	 * Post to register a order cancellation
	 * @param formValues object with all the info
	 * @author Castor Transformación Digital - Fabio Oquendo
	 * @since 2021-02-11
	 */
	postRegisterCancelOrders(formValues: OrderCancelModel): Observable<string> {
		const type = 'text' as 'json';
		return this.httpC.post<string>(this.API_REGISTER_CANCEL_ORDER_URL, formValues, { responseType: type });
	}

	/**
	 * Get method to validate the shipping date
	 * @param division
	 * @param shippingDate
	 * @author Castor Transformación Digital - Andrés Montilla
	 * @since 2022-01-21
	 */
	validateShippingDate(division: string, shippingDate: string): Observable<ValidateShippingDateInformation> {
		const paramsForReq = new HttpParams()
			.set('division', division)
			.set('shippingDate', shippingDate);
		return this.httpC.get<ValidateShippingDateInformation>(this.API_VALIDATE_SHIPPING_DATE_URL, { params: paramsForReq });
	}
	/**
		 * Search sale order by filter params (division, warehouse, dateFrom, dateTo, dateType, orderNumbers, customers, poNumber, salesperson)
		 * @param filters
		 * @author Castor Transformación Digital - Andrés Montilla
		 * @since 2022-05-11
		 */
	searchSaleOrder(filters: any): Observable<any> {
		return this.httpC.post<any>(this.API_SALE_ORDER_SEARCH_URL, filters);
	}
	generateASN(orderNumber: string, division:string): Observable<any> {
		 const paramsForReq = new HttpParams()
		   .set('orderNumber', String(orderNumber))
		   .set('division', String(division));
	   const type = 'text' as 'json';
		return this.httpC.post<any>(this.API_ASN_URL, paramsForReq, { responseType: type });
	}

	/**
	 * Trigger weekly non-EDI report scheduler process
	 * @author Castor Transformación Digital
	 * @since 2026-03-04
	 */
	getWeeklyNonEdiReportScheduler(): Observable<any> {
		const type = 'text' as 'json';
		return this.httpC.get<any>(this.API_WEEKLY_NON_EDI_REPORT_SCHEDULER_URL, { responseType: type });
	}

}
